import {
  Link as MaterialLink,
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import { grey, green, amber, red } from "@mui/material/colors";
import { useDispatch } from "react-redux";
import { addSymbol } from "../reducers/symbolReducer";
import { MarketPump } from "../types/Market";
import { AccessTime, Circle } from "@mui/icons-material";

interface ComponentProps {
  items: MarketPump[];
  krwUSDT: number;
  sortByVolume: boolean;
  bybitMarkets: String[];
}

const usdtLocale = Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const usdtLocale2 = Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumSignificantDigits: 7,
  minimumFractionDigits: 2,
  maximumFractionDigits: 12,
});

const getColor = (movement: number) => {
  if (movement > 0) {
    if (movement >= 0.07) {
      return green[700];
    } else if (movement >= 0.06) {
      return green[600];
    } else if (movement >= 0.05) {
      return green[500];
    } else if (movement >= 0.04) {
      return green[400];
    } else if (movement >= 0.03) {
      return green[300];
    } else if (movement >= 0.02) {
      return green[200];
    } else if (movement >= 0.01) {
      return green[100];
    }
  } else if (movement < 0) {
    if (movement <= -0.07) {
      return amber[700];
    } else if (movement <= -0.06) {
      return amber[600];
    } else if (movement <= -0.05) {
      return amber[500];
    } else if (movement <= -0.04) {
      return amber[400];
    } else if (movement <= -0.03) {
      return amber[300];
    } else if (movement <= -0.02) {
      return amber[200];
    } else if (movement <= -0.01) {
      return amber[100];
    }
  }

  return grey[500];
};

function PumpTable(props: ComponentProps) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const hiddenOnMobile = isSmall ? { display: 'none' } : {};
  const movementDecimals = isSmall ? 1 : 2;

  return (
    <TableContainer>
      <Table size={isSmall ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            <TableCell>Coin</TableCell>
            <TableCell sx={{ ...hiddenOnMobile }}>Latest date</TableCell>
            <TableCell sx={{ textAlign: "right", ...hiddenOnMobile }}>Price (USDT)</TableCell>
            <TableCell sx={{ textAlign: "right", ...hiddenOnMobile }}>
              Volume (USDT) [% of 24h]
            </TableCell>
            <TableCell sx={{ textAlign: "right" }}>{isSmall ? '%' : 'Movement (%)'}</TableCell>
            <TableCell sx={{ textAlign: "right" }}>{isSmall ? '% max' : 'Movement (% max)'}</TableCell>
            <TableCell sx={{ textAlign: "right" }}>{isSmall ? 'Dis' : 'Dislocation'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.items.map((n) => {
            let percent_24h = n.volume / (n.volume_24h - n.volume);

            return (
              <TableRow key={n.coin}>
                <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                    {
                    props.bybitMarkets.includes(n.coin) ? (
                        <Tooltip title="Bybit Market" placement="top">
                            <Circle sx={{ fontSize: 8, color: green[500], mr: 0.5 }} />
                        </Tooltip>
                    ) : <Tooltip title="No Bybit Market" placement="top">
                            <Circle sx={{ fontSize: 8, color: red[500], mr: 0.5 }} />
                        </Tooltip>
                  }
                   &nbsp;
                  <MaterialLink
                    sx={{ textDecoration: "none", cursor: "pointer" }}
                    onClick={() => {
                      dispatch(
                        addSymbol({ symbol: n.coin || "", pinned: true })
                      );
                    }}
                  >
                    {n.coin}
                  </MaterialLink>
                  {isSmall && (Date.now() - n.latest_timestamp) > 10000 && (
                    <Tooltip title={`${Math.round((Date.now() - n.latest_timestamp) / 1000)}s ago`} placement="top">
                      <AccessTime sx={{ fontSize: 14, color: amber[700], ml: 0.5 }} />
                    </Tooltip>
                  )}
                 
                  
                  </Box>
                </TableCell>
                <TableCell sx={{ ...hiddenOnMobile }}>
                  {DateTime.fromMillis(n.latest_timestamp)
                    .toUTC()
                    .toFormat("yyyy-MM-dd HH:mm:ss")}Z
                </TableCell>
                <TableCell sx={{ textAlign: "right", ...hiddenOnMobile }}>
                  {usdtLocale2.format(n.latest_price / (props.krwUSDT ?? 1))}
                </TableCell>
                <TableCell sx={{ textAlign: "right", ...hiddenOnMobile }}>
                  {usdtLocale.format(
                    (n.volume * n.latest_price) / (props.krwUSDT ?? 1)
                  )}
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ color: percent_24h > 0.02 ? null : grey[500] }}
                  >
                    &nbsp;[
                    {(n.volume / (n.volume_24h - n.volume)).toLocaleString(
                      undefined,
                      {
                        style: "percent",
                        minimumFractionDigits: 2,
                      }
                    )}
                    ]
                  </Typography>
                </TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "end",
                    }}
                  >
                    {(n.movement / 100.0).toLocaleString(undefined, {
                      style: "percent",
                      minimumFractionDigits: movementDecimals,
                      maximumFractionDigits: movementDecimals,
                    })}
                    <Circle
                      fontSize="small"
                      sx={{ m: 0, p: 0, ml: isSmall ? 0.5 : 1 }}
                      htmlColor={getColor(n.movement / 100.0)}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "end",
                    }}
                  >
                    {(n.movementMax / 100.0).toLocaleString(undefined, {
                      style: "percent",
                      minimumFractionDigits: movementDecimals,
                      maximumFractionDigits: movementDecimals,
                    })}
                    <Circle
                      fontSize="small"
                      sx={{ m: 0, p: 0, ml: isSmall ? 0.5 : 1 }}
                      htmlColor={getColor(n.movementMax / 100.0)}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  {n.referencePrice
                    ? (() => {
                        const dislocation = (n.latest_price / (props.krwUSDT ?? 1)) / n.referencePrice - 1.0;
                        return (
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{ color: Math.abs(dislocation) > 0.01 ? red[500] : grey[700] }}
                          >
                            {dislocation.toLocaleString(undefined, {
                              style: "percent",
                              minimumFractionDigits: movementDecimals,
                              maximumFractionDigits: movementDecimals,
                            })}
                          </Typography>
                        );
                      })()
                    : '—'
                  }
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default PumpTable;
